import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import {
  WrenchScrewdriverIcon,
  HomeIcon,
  ArrowLeftIcon,
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";

//components
import { SectionHeader } from "../../components/layouts/section.header";
import { SectionSubHeader } from "../../components/layouts/section.subheader";
import { JobIdLink } from "../../components/commons/job.id.link";
import { BadgeServiceStatus } from "../../components/commons/badge.service.status";
import { SearchBox } from "../../components/commons/search.box";

//stores
import {
  fetchCustomerJobs,
  fetchCustomerPremises,
  fetchCustomerProfile,
  clearCustomerDetail,
} from "../../stores/customer";

export const ScreenCustomerDetails = () => {
  const dispatch = useDispatch();
  const { user_id = null } = useParams();
  const { state } = useLocation(); // { name, email, phone_number, status, enabled }

  const { jobs = [], premises = [], profile = null, loading = false } = useSelector(
    (c) => c.customer,
  );

  // Prefer router state (instant on click-through), fall back to fetched profile
  const customer = {
    name: state?.name || profile?.name,
    email: state?.email || profile?.email,
    phone_number: state?.phone_number || profile?.phone_number,
    status: state?.status || profile?.status,
    enabled: state?.enabled !== undefined ? state.enabled : profile?.enabled,
  };

  const [jobSearch, setJobSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("all"); 
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  // Recent Jobs searchbox and filter
  const JOB_SEARCH_MIN = 4;
  const jobQuery = jobSearch.trim();

  const inDateRange = (created) => {
    if (!created) return false;
    const d = moment(created);
    switch (dateFilter) {
      case "today":
        return d.isSame(moment(), "day");
      case "week":
        return d.isSame(moment(), "isoWeek"); 
      case "month":
        return d.isSame(moment(), "month"); 
      case "custom":
        if (!customStart || !customEnd) return true; 
        return d.isBetween(
          moment(customStart).startOf("day"),
          moment(customEnd).endOf("day"),
          null,
          "[]",
        );
      default:
        return true; 
    }
  };

  const filteredJobs = jobs.filter((j) => {
    // date filter
    if (!inDateRange(j.created)) return false;
    // job-id search (only once >= min chars)
    if (jobQuery.length >= JOB_SEARCH_MIN) {
      return (j.job_id || "").toLowerCase().includes(jobQuery.toLowerCase());
    }
    return true;
  });

  const phaseLabel = (p) =>
    ({
      single_phase: "Single phase",
      three_phase: "3-phase",
      three_phase_ct: "3-phase CT",
    }[p] || "-");

  const dateFilterLabel = () => {
    switch (dateFilter) {
      case "today":
        return "today";
      case "week":
        return "this week";
      case "month":
        return "this month";
      case "custom":
        return customStart && customEnd
          ? `${moment(customStart).format("DD MMM YYYY")} – ${moment(customEnd).format("DD MMM YYYY")}`
          : "the selected date range";
      default:
        return "all time";
    }
  };

  // dashboard stats, derived from fetched data (no extra calls)
  const totalJobs = jobs.length;
  const completedJobs = jobs.filter((j) =>
    (j.status || "").includes("COMPLETED"),
  ).length;
  const openJobs = totalJobs - completedJobs;
  const totalPremises = premises.length;

  useEffect(() => {
    if (!user_id) return;

    dispatch(fetchCustomerJobs({ user_id }))
      .unwrap()
      .catch((err) => {
        console.error("screens.customers.details@load", err);
        toast.error("Failed to load customer jobs");
      });

    dispatch(fetchCustomerPremises({ user_id }))
      .unwrap()
      .catch((err) => {
        console.error("screens.customers.details@loadPremises", err);
      });
    
    dispatch(fetchCustomerProfile({ user_id }))
      .unwrap()
      .catch((err) => {
        console.error("screens.customers.details@loadProfile", err);
      });

    return () => dispatch(clearCustomerDetail());
  }, [user_id, dispatch]);

  return (
    <>
      <Helmet>
        <title>Customer Dashboard</title>
      </Helmet>

      <div className="min-h-screen w-full bg-slate-50">
        {/* HEADER */}
        <div className="border-b border-slate-200 bg-white px-8 py-6">
          <Link
            to="/customers/list"
            className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            Back to List Customer
          </Link>

          <p className="text-xs font-semibold uppercase tracking-wide text-primary-500">
            Customer Dashboard
          </p>
           <div className="mt-1 flex items-center gap-3">
            <UserCircleIcon className="h-10 w-10 text-slate-300" aria-hidden="true" />
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                {customer.name || "Customer"}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                {customer.email && (
                  <span className="inline-flex items-center gap-1">
                    <EnvelopeIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                    {customer.email}
                  </span>
                )}
                {customer.phone_number && (
                  <span className="inline-flex items-center gap-1">
                    <PhoneIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                    {customer.phone_number}
                  </span>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* STAT CARDS */}
        <div className="grid grid-cols-2 gap-4 px-8 pt-6 sm:grid-cols-4">
          <StatCard label="Total Jobs" value={totalJobs} />
          <StatCard label="Completed" value={completedJobs} />
          <StatCard label="Open / Active" value={openJobs} />
          <StatCard label="Premises" value={totalPremises} />
        </div>

        

        {/* BODY: two columns */}
        <div className="grid grid-cols-12 gap-6 px-8 py-6">
          {/* LEFT: jobs + premises */}
          <div className="col-span-12 space-y-8 lg:col-span-8">
            {/* RECENT JOBS */}
            <div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <SectionSubHeader Icon={WrenchScrewdriverIcon} title="Recent Jobs" />
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <JobDateFilter
                    value={dateFilter}
                    onChange={setDateFilter}
                    customStart={customStart}
                    customEnd={customEnd}
                    setCustomStart={setCustomStart}
                    setCustomEnd={setCustomEnd}
                  />
                  <SearchBox
                    value={jobSearch}
                    onChange={setJobSearch}
                    placeholder="Search by job id (min 4 digits)..."
                  />
                </div>
              </div>

              <div className="mt-4 overflow-x-auto rounded-md bg-white shadow-md">

                {loading && (
                  <div className="p-8 text-center text-sm text-gray-500">
                    Loading jobs...
                  </div>
                )}

                {!loading && jobs.length === 0 && (
                  <div className="p-8 text-center text-sm text-gray-500">
                    No jobs found for this customer.
                  </div>
                )}

                {!loading && jobs.length > 0 && filteredJobs.length === 0 && (
                  <div className="p-8 text-center text-sm text-gray-500">
                    {jobQuery.length >= JOB_SEARCH_MIN
                      ? `No jobs match "${jobQuery}".`
                      : `No jobs found for ${dateFilterLabel()}.`}
                  </div>
                )}

                {!loading && filteredJobs.length > 0 && (
                  <table className="min-w-full">

                    <thead className="border-y border-slate-200 bg-slate-100">
                      <tr>
                        <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Job ID</th>
                        <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Status</th>
                        <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Job Type</th>
                        <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Premise</th>
                        <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Phase Type</th>
                        <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Amount</th>
                        <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Created</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {filteredJobs.map((job) => (
                        <tr key={job.job_id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 text-sm">
                            <JobIdLink user_id={job.user_id} job_id={job.job_id} />
                          </td>
                          <td className="px-4 py-4">
                            <BadgeServiceStatus status={job.status} />
                          </td>
                          {/* Job Type */}
                          <td className="px-4 py-4 text-sm capitalize text-slate-600">
                            {job.service_type || "-"}
                          </td>
                          {/* Premise: name + type as grey subtext + location */}
                          <td className="px-4 py-4 text-sm">
                            <p className="text-slate-800">{job.premise?.name || "-"}</p>
                            {job.premise?.type && (
                              <p className="text-xs text-slate-400">{job.premise.type}</p>
                            )}
                            {(job.premise?.place?.municipality || job.premise?.place?.region) && (
                              <p className="text-xs text-slate-400">
                                {[job.premise?.place?.municipality, job.premise?.place?.region]
                                  .filter(Boolean)
                                  .join(", ")}
                              </p>
                            )}
                          </td>
                          {/* Phase Type */}
                          <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-600">
                            {phaseLabel(job.premise?.phase_type)}
                          </td>
                          {/*Amount */}
                          <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-800">
                            {typeof job.invoice_amount_paid === "number"
                              ? `${(job.invoice_currency || "myr").toUpperCase()} ${(job.invoice_amount_paid / 100).toFixed(2)}`
                              : "-"}
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-600">
                            {job.created ? moment(job.created).format("DD MMM YYYY") : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* PREMISES */}
            <div>
              <SectionSubHeader Icon={HomeIcon} title="Premises" />
              <div className="mt-4 overflow-x-auto rounded-md bg-white shadow-md">
                {!loading && premises.length === 0 && (
                  <div className="p-8 text-center text-sm text-gray-500">
                    No premises found for this customer.
                  </div>
                )}

                {premises.length > 0 && (
                  <table className="min-w-full">
                    <thead className="border-y border-slate-200 bg-slate-100">
                      <tr>
                        <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Name</th>
                        <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Type</th>
                        <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Location</th>
                        <th className="whitespace-nowrap px-4 py-4 text-left text-sm">Address</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {premises.map((p, idx) => (
                        <tr key={p.id || p.premise_id || idx} className="hover:bg-gray-50">
                          <td className="px-4 py-4 text-sm text-slate-800">{p.name || "-"}</td>
                          <td className="px-4 py-4 text-sm text-slate-600">{p.type || "-"}</td>
                          <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-600">
                            {[
                              p.place?.municipality || p.address?.city,
                              p.place?.state || p.address?.state,
                            ]
                              .filter(Boolean)
                              .join(", ") || "-"}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            {[
                              p.address?.address1,
                              p.address?.address2,
                              p.address?.poscode,
                              p.address?.city,
                              p.address?.state,
                              p.address?.country,
                            ]
                              .filter(Boolean)
                              .join(", ") || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const StatCard = ({ label, value }) => (
  <div className="rounded-md bg-white p-4 shadow-sm ring-1 ring-slate-100">
    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
    <p className="mt-1 text-2xl font-bold text-slate-800">{value}</p>
  </div>
)


const DATE_PRESETS = [
  { value: "all", label: "All" },
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "custom", label: "Select Date" },
];

const JobDateFilter = ({
  value,
  onChange,
  customStart,
  customEnd,
  setCustomStart,
  setCustomEnd,
}) => (
  <div className="flex flex-col gap-2">
    <div className="flex flex-wrap gap-1">
      {DATE_PRESETS.map((p) => (
        <button
          key={p.value}
          type="button"
          onClick={() => onChange(p.value)}
          className={
            value === p.value
              ? "rounded-md bg-primary-500 px-3 py-1 text-xs font-medium text-white"
              : "rounded-md bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200"
          }
        >
          {p.label}
        </button>
      ))}
    </div>

    {value === "custom" && (
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          value={customStart}
          onChange={(e) => setCustomStart(e.target.value)}
          className="rounded-md border border-gray-300 px-2 py-1 text-xs"
          aria-label="Start date"
        />
        <span className="text-xs text-slate-400">to</span>
        <input
          type="date"
          value={customEnd}
          onChange={(e) => setCustomEnd(e.target.value)}
          className="rounded-md border border-gray-300 px-2 py-1 text-xs"
          aria-label="End date"
        />
      </div>
    )}
  </div>
);

